import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useState } from 'react';
import { mfaVerify, fetchMe } from '@/api/auth';
import { useAuth } from '@/lib/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const schema = z.object({
  code: z.string().regex(/^\d{6}$/, '6-digit code required'),
});
type Values = z.infer<typeof schema>;

export function MfaVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const challengeToken = (location.state as { challengeToken?: string } | null)?.challengeToken;
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  // Deviation A: use Navigate component instead of navigate() during render
  if (!challengeToken) return <Navigate to="/login" replace />;

  async function onSubmit(values: Values) {
    setError(null);
    try {
      const r = await mfaVerify(challengeToken as string, values.code);
      useAuth.setState({ accessToken: r.access_token });
      const me = await fetchMe();
      useAuth.getState().setSession({
        accessToken: r.access_token,
        user: me.user,
        tenant: me.tenant,
      });
      if (me.user.must_change_password) navigate('/set-password');
      else navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    }
  }

  return (
    <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <Card>
        <CardHeader>
          <CardTitle>Enter verification code</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-1">
              <Label htmlFor="code">6-digit code</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                aria-invalid={errors.code != null}
                aria-describedby={errors.code ? 'code-error' : undefined}
                {...register('code')}
              />
              {errors.code && (
                <p id="code-error" role="alert" className="text-xs text-destructive">
                  {errors.code.message}
                </p>
              )}
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Verifying…' : 'Verify'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
